import { Box, Button, Flex, Input, Text } from "@mantine/core";
import {
  IconSearch,
  IconTransactionEuro,
  IconZoomReset,
} from "@tabler/icons-react";
import {
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import * as React from "react";
import { useDeferredValue, useEffect, useState } from "react";
import { _localization } from "../../../config/location";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { getDataEmployeePOS } from "../../../api/ApiEmployee";
import { useDebouncedState } from "@mantine/hooks";
import {
  addUserToGroup,
  getDataUserByGroup,
  getUserAuthByGroup,
  groupAPIUserAdd,
} from "../../../api/ApiAdmin";
import { modals } from "@mantine/modals";
import { AddUserToGroup } from "../../../model/MenuGroup";

const GroupAPIEmployeeAdd = ({ idGroup }: { idGroup: string }) => {
  let idsEmp: Array<string> = [];
  //#region  table

  //data and fetching state
  const [data, setData] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState<number>(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100,
  });
  //table
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [selectIds, setSelectIds] = useState<string[]>([]);
  const rowSelectionDeferred =
    useDeferredValue<MRT_RowSelectionState>(rowSelection);
  const [loadingButton, setLoadingButton] = useState<boolean>(false);
  // const [sortData, setSortData] = useState<boolean>(true);
  const [startGetdata, setStartGetData] = useState<boolean>(false);
  const [keySearch, setKeySearch] = useDebouncedState<string>("", 300);

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    try {
      const res = await getDataEmployeePOS({
        keySearch: keySearch,
        skip: pagination.pageIndex * pagination.pageSize,
        take: pagination.pageSize,
        listIdSort:
          selectIds.length > 0 ? selectIds.map((item) => Number(item)) : [],
      });

      if (res && res.success) {
        const result = res.data;
        setData(result);
        setRowCount(res.totalCount);
      }
    } catch (error) {
      setIsError(true);
      console.error(error);
      return;
    }
    setIsError(false);
    setIsLoading(false);
    setIsRefetching(false);
  };

  const getUserByGroup = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    try {
      const res = await getUserAuthByGroup(idGroup);

      if (res && res.success) {
        const result = res.data?.map((item: any) => item.userId);
        const dataRowSelection = res.data?.reduce(
          (acc: Record<string, boolean>, item: any) => {
            acc[item.userId] = true;
            return acc;
          },
          {}
        );
        setSelectIds(result);
        setRowSelection(dataRowSelection);
        setStartGetData(true);
      } else {
        setStartGetData(true);
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      setIsRefetching(false);
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  const handleAddUserToGroup = async () => {
    setLoadingButton(true);
    try {
      const dataSubmit = {
        groupId: idGroup,
        usersIds: selectIds,
      };
      const res = await groupAPIUserAdd(dataSubmit);

      if (res && res.success) {
        NotificationExtension.Success("Phân quyền thành công !");
        setLoadingButton(false);
        modals.closeAll();
      } else {
        NotificationExtension.Fails(res.message);
      }
    } catch (error) {
      console.log(error);
      setLoadingButton(false);
    } finally {
      setLoadingButton(false);
    }
  };

  useEffect(() => {
    const valuesList = Object.keys(rowSelectionDeferred);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelectionDeferred);
      setSelectIds(valuesList);
    }
  }, [rowSelectionDeferred]);

  useEffect(() => {
    if (startGetdata) {
      fetchData();
    }
  }, [pagination.pageIndex, startGetdata]);

  useEffect(() => {
    getUserByGroup();
  }, [idGroup]);

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "fullname",
        header: "Họ và tên",
        Cell: ({ row }) => (
          <Text fw={700} size="11.5px">
            {row.original.code} - {row.original.fullname}
          </Text>
        ),
      },
      {
        accessorKey: "roleName",
        header: "Chức vụ",
      },
      {
        accessorKey: "departmentName",
        header: "Phòng ban",
      },
      {
        accessorKey: "branchId",
        header: "Chi nhánh",
      },
    ],
    []
  );

  const table = useMantineReactTable({
    columns,
    data,
    positionToolbarAlertBanner: "bottom",
    enableRowVirtualization: true,
    enableRowNumbers: true,
    //add custom action buttons to top-left of top toolbar
    renderTopToolbarCustomActions: ({ table }) => (
      <>
        <Flex
          gap="md"
          justify="flex-start"
          align="center"
          direction="row"
          wrap="wrap"
        >
          <Input
            placeholder="Nhập từ khóa ..."
            defaultValue={keySearch}
            onChange={(e) => setKeySearch(e.currentTarget.value)}
          />
          <Button
            leftSection={<IconSearch size={14} />}
            variant="outline"
            onClick={() => fetchData()}
          >
            Tìm kiếm
          </Button>
        </Flex>
      </>
    ),
    renderToolbarInternalActions: ({ table }) => (
      <>
        <Flex
          gap="md"
          justify={{ sm: "flex-start", lg: "flex-end" }}
          align="center"
          direction="row"
          wrap="wrap"
        >
          <Button
            leftSection={<IconTransactionEuro size={14} />}
            color=""
            variant="outline"
            loading={loadingButton}
            onClick={() => {
              handleAddUserToGroup();
            }}
          >
            Phân quyền
          </Button>
          <Button
            leftSection={<IconZoomReset size={14} />}
            color="violet"
            variant="outline"
            onClick={async () => {
              await fetchData();
            }}
          >
            Load data
          </Button>
        </Flex>
      </>
    ),

    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: true,
    getRowId: (row) => row.id?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        left: ["mrt-row-select"],
      },
      columnVisibility: { id: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: "60vh", minHeight: "50vh" },
    },
    enableStickyHeader: true,
    manualPagination: true,
    onRowSelectionChange: setRowSelection,
    // rowCount: data.length,
    rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: "bold",
        fontSize: "11px",
        padding: "5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading,
      rowSelection,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    rowVirtualizerOptions: {
      overscan: 5,
    },
  });
  return (
    <Box mt={10} w={"82.5vw"} maw={1250}>
      <MantineReactTable table={table} />
    </Box>
  );
};
export default GroupAPIEmployeeAdd;
